import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Sparkles, Calendar, CheckCircle2, AlertCircle, Clock, Play } from 'lucide-react';
import { ForecastTemplate, AIGenerationLog, ZODIAC_SIGNS, FORECAST_TYPE_LABELS, formatDateTimeFull } from '../../data/forecastCmsData';
interface AdminForecastAIGeneratorProps {
  templates: ForecastTemplate[];
  logs: AIGenerationLog[];
  setLogs: React.Dispatch<React.SetStateAction<AIGenerationLog[]>>;
  success: (msg: string) => void;
}
export function AdminForecastAIGenerator({
  templates,
  logs,
  setLogs,
  success
}: AdminForecastAIGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [form, setForm] = useState({
    type: 'day',
    periodStart: new Date().toISOString().split('T')[0],
    periodEnd: new Date(Date.now() + 6 * 86400000).toISOString().split('T')[0],
    templateId: templates[0]?.id || '',
    signs: ZODIAC_SIGNS.map(z => z.key),
    lang: 'ru'
  });
  const handleToggleSign = (signKey: string) => {
    setForm(prev => ({
      ...prev,
      signs: prev.signs.includes(signKey as any) ? prev.signs.filter(s => s !== signKey) : [...prev.signs, signKey as any]
    }));
  };
  const handleGenerate = () => {
    if (form.signs.length === 0) {
      alert('Выберите хотя бы один знак зодиака');
      return;
    }
    setIsGenerating(true);
    // Simulate API call
    setTimeout(() => {
      const newLog: AIGenerationLog = {
        id: `aig-${Date.now()}`,
        generation_type: 'bulk',
        forecast_type: form.type as any,
        signs: form.signs as any,
        period_start: form.periodStart,
        period_end: form.periodEnd,
        lang: form.lang as any,
        template_id: form.templateId,
        status: 'completed',
        total_items: form.signs.length * 7,
        completed_items: form.signs.length * 7,
        failed_items: 0,
        created_at: new Date().toISOString(),
        created_by: 'Администратор',
        error_message: null
      };
      setLogs([newLog, ...logs]);
      setIsGenerating(false);
      success('Генерация успешно завершена. Прогнозы добавлены в черновики.');
    }, 3000);
  };
  return <div className="space-y-6" data-id="element-4152">
      <div data-id="element-4153">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-4154">
          AI Генератор прогнозов
        </h2>
        <p className="text-slate-500" data-id="element-4155">
          Массовое создание прогнозов с помощью нейросети по шаблонам
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4156">
        <div className="lg:col-span-2" data-id="element-4157">
          <Card padding="lg" className="space-y-6" data-id="element-4158">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4159">
              <div className="space-y-4" data-id="element-4160">
                <div data-id="element-4161">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4162">
                    Тип прогноза
                  </label>
                  <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={form.type} onChange={e => setForm({
                  ...form,
                  type: e.target.value
                })} data-id="element-4163">
                    {Object.entries(FORECAST_TYPE_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-4164">
                        {val.ru}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-4165">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4166">
                    Шаблон генерации
                  </label>
                  <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={form.templateId} onChange={e => setForm({
                  ...form,
                  templateId: e.target.value
                })} data-id="element-4167">
                    {templates.filter(t => t.forecast_type === form.type).map(t => <option key={t.id} value={t.id} data-id="element-4168">
                          {t.title}
                        </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-4" data-id="element-4169">
                <div data-id="element-4170">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4171">
                    Период с
                  </label>
                  <Input type="date" value={form.periodStart} onChange={e => setForm({
                  ...form,
                  periodStart: e.target.value
                })} data-id="element-4172" />
                </div>
                <div data-id="element-4173">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4174">
                    Период по
                  </label>
                  <Input type="date" value={form.periodEnd} onChange={e => setForm({
                  ...form,
                  periodEnd: e.target.value
                })} data-id="element-4175" />
                </div>
              </div>
            </div>

            <div data-id="element-4176">
              <div className="flex items-center justify-between mb-2" data-id="element-4177">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4178">
                  Знаки зодиака
                </label>
                <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium" onClick={() => setForm({
                ...form,
                signs: form.signs.length === 12 ? [] : ZODIAC_SIGNS.map(z => z.key)
              })} data-id="element-4179">
                  {form.signs.length === 12 ? 'Снять все' : 'Выбрать все'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-id="element-4180">
                {ZODIAC_SIGNS.map(z => {
                const isSelected = form.signs.includes(z.key as any);
                return <div key={z.key} onClick={() => handleToggleSign(z.key)} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-4181">
                      <span className="text-lg" data-id="element-4182">{z.symbol}</span>
                      <span className="font-medium" data-id="element-4183">{z.ru}</span>
                    </div>;
              })}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-4184">
              <div className="text-sm text-slate-500" data-id="element-4185">
                Будет создано:{' '}
                <strong className="text-slate-900" data-id="element-4186">
                  ~{form.signs.length * 7} черновиков
                </strong>
              </div>
              <Button variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-indigo-600 border-none" onClick={handleGenerate} disabled={isGenerating || form.signs.length === 0} data-id="element-4187">
                {isGenerating ? <span className="flex items-center gap-2" data-id="element-4188">
                    <Sparkles className="w-4 h-4 animate-spin" data-id="element-4189" /> Генерируем...
                  </span> : <span className="flex items-center gap-2" data-id="element-4190">
                    <Play className="w-4 h-4" data-id="element-4191" /> Запустить генерацию
                  </span>}
              </Button>
            </div>
          </Card>
        </div>

        <div data-id="element-4192">
          <Card padding="lg" className="h-full" data-id="element-4193">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-4194">
              История генераций
            </h3>
            <div className="space-y-4" data-id="element-4195">
              {logs.map(log => <div key={log.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-sm" data-id="element-4196">
                  <div className="flex items-center justify-between mb-2" data-id="element-4197">
                    <span className="font-medium text-slate-900" data-id="element-4198">
                      {FORECAST_TYPE_LABELS[log.forecast_type].ru} прогнозы
                    </span>
                    {log.status === 'completed' ? <Badge variant="success" size="sm" data-id="element-4199">
                        <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-4200" /> Успешно
                      </Badge> : log.status === 'in_progress' ? <Badge variant="warning" size="sm" data-id="element-4201">
                        <Clock className="w-3 h-3 mr-1" data-id="element-4202" /> В процессе
                      </Badge> : <Badge variant="error" size="sm" data-id="element-4203">
                        <AlertCircle className="w-3 h-3 mr-1" data-id="element-4204" /> Ошибка
                      </Badge>}
                  </div>
                  <div className="text-xs text-slate-500 space-y-1" data-id="element-4205">
                    <div className="flex items-center gap-1.5" data-id="element-4206">
                      <Calendar className="w-3 h-3" data-id="element-4207" /> {log.period_start} —{' '}
                      {log.period_end}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-4208">
                      <Sparkles className="w-3 h-3" data-id="element-4209" /> Сгенерировано:{' '}
                      {log.completed_items}/{log.total_items}
                    </div>
                    <div className="text-slate-400 mt-2" data-id="element-4210">
                      {formatDateTimeFull(log.created_at)}
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}